import React, { useEffect, useState } from 'react'
import { ShimmerText, ShimmerTitle, ShimmerButton } from 'react-shimmer-effects'
import { useHistory, useParams } from 'react-router-dom'
import { Col, Row } from 'react-bootstrap'
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import {
  Button,
  useErrorService,
  useUserService,
  useNotificationService,
  AddCreditCard,
} from '@abroad/components'
import CardSelectionCheckbox from './CardSelectionCheckbox'
import DeleteCard from './DeleteCard'
import API from '../../utils/API'
import classNames from 'classnames'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)

const CardsListing = ({
  selectedCardDetails,
  setSelectedCardDetails = () => {},
  setShowSelectedCard = () => {},
  isDiscardOnsubmit = true,
  enabledUncheckedAction = false,
  onContinue = () => {},
  hideContinueButton = false,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCardId, setSelectedCardId] = useState(
    selectedCardDetails ? selectedCardDetails?.id : null,
  )
  const [isToggleCardListing, setIsToggleCardListing] = useState(false)
  const [isCardChanging, setIsCardChanging] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [isAddingCard, setIsAddingCard] = useState(false)
  const [stripeLoading, setStripeLoading] = useState(true)
  const { coachingPlanId, cardId } = useParams()
  const [cards, setCards] = useState([])
  const Error = useErrorService()
  const Notification = useNotificationService()
  const history = useHistory()
  const { user } = useUserService()
  const stripe = useStripe()
  const elements = useElements()

  useEffect(() => {
    if (stripe) {
      setStripeLoading(false)
    }
  }, [stripe])

  useEffect(() => {
    if (isDiscardOnsubmit) {
      setSelectedCardDetails({ id: selectedCardId })
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCardId])

  const addCard = async () => {
    try {
      setIsAddingCard(true)
      const data = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement('cardNumber'),
        billing_details: {
          name: user?.fullName,
          email: user?.email,
          address: {},
        },
      })
      if (data && !data?.error) {
        setShowSelectedCard(data?.paymentMethod)
        const { data: cardData } = await API.payment.attachPayMethod({
          paymentMethodId: data?.paymentMethod?.id,
        })
        if (cardData) {
          setIsAddingCard(false)
          setShowForm(false)
          setSelectedCardDetails({ id: data?.paymentMethod?.id })
          setSelectedCardId(data?.paymentMethod?.id)
          setIsToggleCardListing(!isToggleCardListing)
          onContinue()
        }
      } else {
        setIsAddingCard(false)
      }
    } catch (e) {
      setIsAddingCard(false)
      // Error.showError(e)
      Notification.showNotification(e?.message, 'danger')
    }
  }

  const changeSubscriptionCardHandler = async () => {
    setIsCardChanging(true)
    try {
      const { data } = await API.payment.changeSubscriptionCard({
        subscriptionId: coachingPlanId,
        paymentMethodId: selectedCardId,
      })
      if (data) {
        setIsCardChanging(false)
        // redirect to plan & payment page
        history.replace('/plans-payments')
      }
    } catch (e) {
      setIsCardChanging(false)
      setSelectedCardId(cardId)
      Error.showError(
        e?.code === 'payment_fail'
          ? {
              code: e?.code,
              message: 'Your payment is not completed. Please try again.',
            }
          : e,
      )
    }
  }

  useEffect(() => {
    const isCardMatch = cards?.find((card) => card?.id === selectedCardId)
    if (cards?.length === 0) {
      setSelectedCardDetails(null)
    } else if (!isCardMatch && cards.length > 0) {
      // If the selected card is not in the list, select the first card
      setSelectedCardDetails(cards[0])
      setSelectedCardId(cards[0]?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards])

  useEffect(() => {
    const getUserCardListing = async () => {
      setIsLoading(true)
      try {
        const { data: response } = await API.payment.getUserCardListing()
        if (response.data && response.data?.length > 0) {
          if (!selectedCardDetails) {
            setSelectedCardDetails(response.data[0])
            setSelectedCardId(response.data[0]?.id)
          }
          setCards(response.data)
          setIsLoading(false)
        } else {
          setSelectedCardDetails(null)
          setIsLoading(false)
          if (cards?.length === 0) {
            setShowForm(true)
          }
        }
      } catch (e) {
        setIsLoading(false)
        Error.showError(e)
      }
    }
    getUserCardListing()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToggleCardListing])

  const renderTitle = (title = ' ') => {
    return (
      <div className='d-flex mb-24px'>
        <h3 className='s6 mb-0'>{title}</h3>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className='saved-cards-shimmer'>
        {renderTitle()}
        {[1, 2, 3].map((number, index) => (
          <ShimmerTitle
            line={1}
            variant='primary'
            className='input-shimmer mb-4'
            key={index}
          />
        ))}
        <ShimmerText line={1} />
      </div>
    )
  }

  return (
    <>
      <Row className='card-listing-wrapper'>
        {!showForm ? (
          <>
            {/* {renderTitle('Saved Cards')} */}
            <div className='py-16px pl-16px pr-16px'>
              <div className='card-listing mb-24px'>
                {cards.map(({ card, id }, index) => (
                  <Col key={index} className='col-12'>
                    <div className='d-flex justify-content-between mb-3'>
                      <div className='d-flex'>
                        <CardSelectionCheckbox
                          selectedId={selectedCardId}
                          setSelectedId={setSelectedCardId}
                          checkboxId={id}
                          disabled={isCardChanging}
                          enabledUncheckedAction={enabledUncheckedAction}
                        />
                        <p className='mb-0 align-self-center s6'>{`**** **** **** ${card?.last4}`}</p>
                      </div>
                      <DeleteCard
                        onSuccess={(cardId) => {
                          setCards((prev) => {
                            const updatedCards = prev.filter(
                              (e) => e?.id !== cardId,
                            )
                            if (updatedCards.length === 0) {
                              setShowForm(true)
                            }
                            return updatedCards
                          })
                        }}
                        cardId={id}
                        isLoading={isCardChanging}
                      />
                    </div>
                  </Col>
                ))}
              </div>
              {!isDiscardOnsubmit && (
                <>
                  {cards.length > 1 && (
                    <div>
                      <Button
                        variant='saffron'
                        size='sm'
                        disabled={!selectedCardId || isCardChanging}
                        isLoading={isCardChanging}
                        onClick={changeSubscriptionCardHandler}
                        className='text-uppercase border-radius-normal text-white hover:text-white my-3'>
                        Save
                      </Button>
                    </div>
                  )}
                </>
              )}
              <span
                onClick={() => setShowForm(true)}
                className={classNames('s11 text-saffron-700', {
                  'cursor-pointer': !isAddingCard && !isCardChanging,
                  'pointer-events-none pe-none': isAddingCard || isCardChanging,
                })}>
                Add New Card
              </span>
            </div>
            {!hideContinueButton && (
              <Button
                variant='saffron'
                className='border-radius-normal border-none mt-2 mt-sm-0 order-1 order-sm-0 text-white'
                disabled={!selectedCardId}
                onClick={() => {
                  const selectedCardObj = cards.find(
                    (card) => card.id === selectedCardId,
                  )
                  setShowSelectedCard(selectedCardObj)
                  onContinue()
                }}>
                CONTINUE
              </Button>
            )}
          </>
        ) : (
          <>
            {renderTitle('Add New Card')}
            <div className='add-card-inline-form'>
              {stripeLoading ? (
                <div className='text-center border-0 mt-32 mb-32px add-card-shimmer'>
                  <ShimmerTitle
                    line={1}
                    variant='primary'
                    className='input-shimmer mb-4'
                  />
                  <ShimmerTitle
                    line={1}
                    variant='primary'
                    className='input-shimmer mt-4'
                  />
                  <div className='d-flex justify-content-between justify-content-lg-end mt-40px'>
                    <ShimmerButton side='lg' />
                    <ShimmerButton side='lg' />
                  </div>
                </div>
              ) : (
                <>
                  <AddCreditCard
                    baseStyle={{
                      iconColor: '#666EE8',
                      color: '#000',
                      lineHeight: '32px',
                      fontWeight: 600,
                      fontFamily: 'Open Sans',
                      fontSize: '0.875rem',
                      letterSpacing: '-0.28px',
                      '::placeholder': {
                        fontSize: '0.75rem',
                        color: '#00000040',
                      },
                    }}
                    labelClasses='mb-4'
                  />
                  <div className='d-flex justify-content-between justify-content-lg-end pt-0 mt-32px '>
                    <Button
                      variant='secondary-light'
                      onClick={() => {
                        setShowForm(false)
                        if (cards?.length === 0) {
                          history.replace('/plans-payments')
                        }
                      }}
                      disabled={isAddingCard}
                      className='secondary-popup-button mr-24px custom-padding-height'>
                      Cancel
                    </Button>
                    <Button
                      disabled={isAddingCard}
                      isLoading={isAddingCard}
                      onClick={addCard}
                      variant='saffron'
                      className='border-radius-normal btn-same-width text-white custom-padding-height'>
                      SAVE
                    </Button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </Row>
    </>
  )
}

const CardListingWithStripeWrap = ({ ...props }) => {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans',
          },
        ],
      }}>
      <CardsListing {...props} />
    </Elements>
  )
}

export default CardListingWithStripeWrap
